import React, { useMemo, useCallback, useContext } from 'react'
import moment from 'moment'
import { BusinessTripRate } from '@/app/models/configuration/businessTripRatesConfigurationModel'
import { useBusinessTripRatesTableContext } from '../context/BusinessTripRatesTableContext'

type RowContextValue = {
  rate: BusinessTripRate
  savedRow?: BusinessTripRate
  handleMoneyValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleCurrencyChange: (name: string, code: string) => void
  handleChangeCountry: (code: string) => void
  handleDateFromChange: (date: string | null) => void
  handleDateToChange: (date: string | null) => void
  handleDeleteClick: () => void
}

const BusinessTripRatesRowContext = React.createContext<RowContextValue | null>(null)

export const useBusinessTripRatesRowContext = () => {
  const context = useContext(BusinessTripRatesRowContext)
  if (!context) {
    throw new Error('useBusinessTripRatesRowContext must be used within BusinessTripRatesRowProvider')
  }
  return context
}

type Props = React.PropsWithChildren<{
  rate: BusinessTripRate
}>

export const BusinessTripRatesRowProvider: React.FC<Props> = ({
  rate,
  children,
}) => {
  const {
    savedRates,
    availableCountries,
    availableCurrencies,
    handleUpdate,
    handleDelete,
  } = useBusinessTripRatesTableContext()

  const savedRow = useMemo(
    () => savedRates.find((sr) => sr.id === rate.id),
    [savedRates, rate.id],
  )

  const handleMoneyValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      handleUpdate(rate.id, { [name]: Number(value) })
    },
    [handleUpdate, rate.id],
  )

  const handleCurrencyChange = useCallback(
    (name: string, code: string) => {
      const curr = availableCurrencies.find((c) => c.code === code)
      if (curr) handleUpdate(rate.id, { [name]: curr })
    },
    [availableCurrencies, handleUpdate, rate.id],
  )

  const handleChangeCountry = useCallback(
    (code: string) => {
      const country = availableCountries.find((c) => c.code === code)
      if (country) handleUpdate(rate.id, { country })
    },
    [availableCountries, handleUpdate, rate.id],
  )

  const handleDateFromChange = useCallback(
    (date: string | null) => {
      handleUpdate(rate.id, { dateFrom: date ? moment(date) : null })
    },
    [handleUpdate, rate.id],
  )

  const handleDateToChange = useCallback(
    (date: string | null) => {
      handleUpdate(rate.id, { dateTo: date ? moment(date) : null })
    },
    [handleUpdate, rate.id],
  )

  const handleDeleteClick = useCallback(() => {
    handleDelete(rate.id)
  }, [handleDelete, rate.id])

  const value = useMemo(
    () => ({
      rate,
      savedRow,
      handleMoneyValueChange,
      handleCurrencyChange,
      handleChangeCountry,
      handleDateFromChange,
      handleDateToChange,
      handleDeleteClick,
    }),
    [
      rate,
      savedRow,
      handleMoneyValueChange,
      handleCurrencyChange,
      handleChangeCountry,
      handleDateFromChange,
      handleDateToChange,
      handleDeleteClick,
    ],
  )

  return (
    <BusinessTripRatesRowContext.Provider value={value}>
      {children}
    </BusinessTripRatesRowContext.Provider>
  )
}
